import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const IMG_CATCH = "/photos/cheffillet.jpg";

const CHAPTERS = [
  {
    no: "01",
    title: "The Catch",
    body: "Clean, fresh seafood — never frozen. Flake, whiting, barramundi and calamari picked for quality and battered to order, the way the coast intended.",
  },
  {
    no: "02",
    title: "The Craft",
    body: "A little Greek soul in every serve. Char-grilled lamb and chicken gyros, hand-wrapped souvlaki, house-made tzatziki and crisp Greek salads — made the way yiayia would want, alongside golden chips and Lilydale free-range chicken.",
  },
  {
    no: "03",
    title: "The Family",
    body: "Proudly run by a warm Greek family and loved by the community, Lucky Corner brings a taste of the Mediterranean to Donvale — fish & chips with heart, heritage, and plenty of philoxenia (that's Greek for looking after your guests).",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 60]);

  return (
    <section id="story" ref={ref} data-testid="story-section" className="relative bg-white py-24 lg:py-40">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <motion.div
          variants={reveal}
          custom={0}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="flex items-center gap-4 mb-16"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--slate)]">Καλώς ήρθατε · Our Story</span>
          <span className="h-px flex-1 bg-[var(--hair)]" />
        </motion.div>

        <motion.h2
          variants={reveal}
          custom={0}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] tracking-tight leading-none mb-14"
        >
          A Greek family, <span className="italic text-[var(--azure)]">a taste of the Mediterranean.</span>
        </motion.h2>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24 items-start">
          {/* sticky image */}
          <div className="lg:sticky lg:top-28">
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2px]" style={{ boxShadow: "0 40px 90px -50px rgba(0,42,84,0.5)" }}>
              <motion.img
                src={IMG_CATCH}
                alt="Filleting fresh fish in-store"
                style={{ y: imgY, scale: 1.15 }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <p className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--slate)]">
              Fresh daily · never frozen
            </p>
          </div>

          {/* chapters */}
          <div className="flex flex-col">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.no}
                variants={reveal}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`chapter-${c.no}`}
                className="grid grid-cols-[auto_1fr] gap-6 lg:gap-10 py-10 border-t border-[var(--hair)] first:border-t-0"
              >
                <span className="font-mono text-[var(--azure)] text-sm pt-2">{c.no}</span>
                <div>
                  <h3 className="font-display text-3xl lg:text-5xl text-[var(--navy)] tracking-tight">{c.title}</h3>
                  <p className="mt-4 text-[var(--slate)] text-base lg:text-lg leading-relaxed max-w-xl">{c.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
